import { ClassType } from '@frontend/common';
import { StatusBadge } from '@frontend/components/common';
import { OrderStatus, OrderStatusName } from '@frontend/order/types';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export const ORDER_STATUS_COLOR_MAP = new Map<string[], ClassType>([
    [[OrderStatusName.ANY], ClassType.DISABLED],
    [[OrderStatusName.PENDING], ClassType.WARNING],
    [[OrderStatusName.PROCESSING], ClassType.INFO],
    [[OrderStatusName.COMPLETED], ClassType.SUCCESS],
    [[OrderStatusName.CANCELLED], ClassType.DANGER]
]);

const ORDER_STATUS_MESSAGE_IDS: Record<OrderStatusName, { id: string; defaultMessage: string; description: string }> = {
    [OrderStatusName.ANY]: { id: 'Objects.ORDER.STATUS_ANY', defaultMessage: 'Any state', description: 'Order state: any state' },
    [OrderStatusName.PENDING]: { id: 'Objects.ORDER.STATUS_PENDING', defaultMessage: 'Pending', description: 'Order state: pending' },
    [OrderStatusName.PROCESSING]: { id: 'Objects.ORDER.STATUS_PROCESSING', defaultMessage: 'Processing', description: 'Order state: processing' },
    [OrderStatusName.COMPLETED]: { id: 'Objects.ORDER.STATUS_COMPLETED', defaultMessage: 'Completed', description: 'Order state: completed' },
    [OrderStatusName.CANCELLED]: { id: 'Objects.ORDER.STATUS_CANCELLED', defaultMessage: 'Cancelled', description: 'Order state: cancelled' }
};

export function useOrderStatusDisplayMap(): Map<string, string> {
    const intl = useIntl();
    return useMemo(
        () =>
            new Map<string, string>(
                (Object.keys(ORDER_STATUS_MESSAGE_IDS) as OrderStatusName[]).map((status) => [status, intl.formatMessage(ORDER_STATUS_MESSAGE_IDS[status])])
            ),
        [intl]
    );
}

export interface OrderStatusBadgeProps {
    status: OrderStatusName | string;
}

export const OrderStatusBadge = ({ status }: OrderStatusBadgeProps) => {
    const displayMap = useOrderStatusDisplayMap();
    const displayMapWithFallback = useMemo(() => {
        if (displayMap.has(status)) return displayMap;
        return new Map(displayMap).set(status, OrderStatus.format(status));
    }, [displayMap, status]);

    return (
        <StatusBadge
            value={status}
            colorMap={ORDER_STATUS_COLOR_MAP}
            displayMap={displayMapWithFallback}
        />
    );
};
